/**
 * ReminderMessagePreview - Preview multi-canal da mensagem de lembrete
 * 
 * Renderiza a mensagem em tempo real conforme o canal da inbox selecionada:
 * - WhatsApp: balão branco com fundo bege, timestamp, check duplo
 * - SMS: balão estilo mensagem nativa
 * - Email: card estilo inbox de email
 * - Telegram: balão azul claro
 * - Fallback genérico para canais desconhecidos
 * 
 * Substitui variáveis {{var}} por valores de exemplo destacados visualmente.
 * 
 * @version 1.0.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do componente premium
 */

import React from 'react';
import { cn } from '@/lib/utils';
import { MessageSquare, Mail, Send, Smartphone, Monitor, CheckCheck, FileText } from 'lucide-react';

// ============================================================================
// TIPOS
// ============================================================================

/** Anexo simulado exibido no preview (ex.: PDF da proposta) */
export interface ReminderPreviewAttachment {
  /** Nome do arquivo exibido no balão de documento */
  fileName: string;
  /** Rótulo auxiliar (ex.: "PDF • 1 página") */
  caption?: string;
}

interface ReminderMessagePreviewProps {
  /** Tipo de canal da inbox selecionada */
  channelType?: string;
  /** Nome da inbox */
  inboxName?: string;
  /** Texto da mensagem com variáveis {{var}} */
  messageText: string;
  /** Tipo de mensagem (text ou template) */
  messageType: string;
  /** Nome do template HSM (quando messageType === 'template') */
  templateName?: string;
  /** Mapeamento de variáveis posicionais → variáveis do sistema (para templates WABA) */
  variableMapping?: Record<string, string>;
  /** Anexo simulado exibido após o balão de texto (opcional) */
  attachment?: ReminderPreviewAttachment | null;
}


// ============================================================================
// VALORES DE EXEMPLO PARA VARIÁVEIS
// ============================================================================

/** Mapeamento de variáveis para valores de exemplo */
const VARIABLE_EXAMPLES: Record<string, string> = {
  '{{nome_completo}}': 'Maria Silva',
  '{{nome_cliente}}': 'João Santos',
  '{{evento}}': 'Consulta Médica',
  '{{data_hora}}': '15/04/2026 às 14:30',
  '{{link_reuniao}}': 'https://meet.exemplo.com/abc',
  '{{link_cancelamento}}': 'https://app.exemplo.com/cancel/abc123',
  '{{valor}}': 'R$ 350,00',
  '{{vencimento}}': '20/04/2026',
  '{{dias_atraso}}': '5',
  '{{payment_link}}': 'https://pay.exemplo.com/xyz',
  '{{descricao}}': 'Mensalidade Abril',
  '{{contrato}}': 'Contrato Anual Premium',
  '{{data_expiracao}}': '31/12/2026',
  '{{nome_deal}}': 'Proposta Comercial XYZ',
  '{{responsavel}}': 'Carlos Mendes',
  '{{fase}}': 'Negociação',
  // Variáveis de agendamento de mensagem (chat)
  '{{contact_name}}': 'Maria Silva',
  '{{first_name}}': 'Maria',
  '{{contact_phone}}': '+55 11 98765-4321',
  '{{agent_name}}': 'Carlos Mendes',
};

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Formata o texto substituindo variáveis por valores de exemplo
 * com destaque visual (spans coloridos)
 */
function formatMessageWithVariables(text: string): React.ReactNode {
  if (!text) return null;

  // Regex para capturar variáveis no formato {{nome_var}}
  const parts = text.split(/(\{\{[a-z_]+\}\})/gi);

  return parts.map((part, index) => {
    if (/\{\{[a-z_]+\}\}/i.test(part)) {
      const example = VARIABLE_EXAMPLES[part] || part;
      return (
        <span
          key={index}
          className="bg-amber-200/80 text-amber-900 px-1 rounded text-xs font-medium"
          title={`Variável: ${part}`}
        >
          {example}
        </span>
      );
    }
    return part;
  });
}

/** Retorna hora atual formatada */
function getCurrentTime(): string {
  return new Date().toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });
}

// ============================================================================
// PREVIEW - WHATSAPP
// ============================================================================

function WhatsAppPreview({
  messageText,
  inboxName,
  attachment,
}: {
  messageText: string;
  inboxName?: string;
  attachment?: ReminderPreviewAttachment | null;
}) {
  return (
    <div className="flex flex-col h-full">
      {/* Barra superior WhatsApp */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#075E54] text-white rounded-t-lg">
        <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
          <MessageSquare className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium truncate">{inboxName || 'WhatsApp'}</p>
          <p className="text-[10px] opacity-70">online</p>
        </div>
      </div>

      {/* Área de chat com fundo WhatsApp */}
      <div
        className="flex-1 p-3 overflow-y-auto min-h-[200px]"
        style={{
          backgroundColor: '#e5ddd5',
          backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ccc\' fill-opacity=\'0.08\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
        }}
      >
        <div className="max-w-[240px] ml-auto">
          {/* Balão de mensagem enviada */}
          <div className="bg-[#dcf8c6] rounded-lg shadow-sm p-2.5 relative">
            <div className="text-[13px] text-slate-900 whitespace-pre-wrap leading-relaxed break-words min-w-0">
              {messageText ? (
                formatMessageWithVariables(messageText)
              ) : (
                <span className="text-slate-400 italic text-xs">
                  Digite a mensagem para ver o preview...
                </span>
              )}
            </div>
            {/* Timestamp + check */}
            <div className="flex items-center justify-end gap-1 mt-1">
              <span className="text-[10px] text-slate-500">{getCurrentTime()}</span>
              <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
            </div>
          </div>

          {/* Balão de documento (anexo simulado) */}
          {attachment && (
            <div className="bg-[#dcf8c6] rounded-lg shadow-sm p-2 mt-1.5">
              <div className="flex items-center gap-2 rounded-md bg-white/70 p-2 min-w-0">
                <div className="w-8 h-8 rounded-md bg-red-100 flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4 text-red-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[12px] font-medium text-slate-900 truncate">
                    {attachment.fileName}
                  </p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {attachment.caption || 'PDF'}
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[10px] text-slate-500">{getCurrentTime()}</span>
                <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>

  );
}

// ============================================================================
// PREVIEW - SMS
// ============================================================================

function SmsPreview({ messageText, inboxName }: { messageText: string; inboxName?: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header SMS */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-700 text-white rounded-t-lg">
        <Smartphone className="w-4 h-4" />
        <p className="text-xs font-medium">{inboxName || 'SMS'}</p>
      </div>

      {/* Área de mensagens */}
      <div className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 overflow-y-auto min-h-[200px]">
        <div className="max-w-[240px] ml-auto">
          <div className="bg-blue-500 text-white rounded-2xl rounded-br-sm px-3 py-2 shadow-sm">
            <div className="text-[13px] whitespace-pre-wrap leading-relaxed break-words min-w-0">
              {messageText ? (
                formatMessageWithVariables(messageText)
              ) : (
                <span className="opacity-50 italic text-xs">
                  Digite a mensagem...
                </span>
              )}
            </div>
          </div>
          <p className="text-[10px] text-slate-400 text-right mt-1">{getCurrentTime()}</p>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PREVIEW - EMAIL
// ============================================================================

function EmailPreview({ messageText, inboxName }: { messageText: string; inboxName?: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header Email */}
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-600 text-white rounded-t-lg">
        <Mail className="w-4 h-4" />
        <p className="text-xs font-medium">{inboxName || 'Email'}</p>
      </div>

      {/* Card de email */}
      <div className="flex-1 p-3 bg-slate-50 dark:bg-slate-800 overflow-y-auto min-h-[200px]">
        <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          {/* Cabeçalho do email */}
          <div className="px-3 py-2 border-b border-slate-100 dark:border-slate-700">
            <p className="text-[11px] text-slate-500">
              <span className="font-medium text-slate-700 dark:text-slate-300">De:</span> {inboxName || 'noreply@empresa.com'}
            </p>
            <p className="text-[11px] text-slate-500">
              <span className="font-medium text-slate-700 dark:text-slate-300">Assunto:</span> Lembrete automático
            </p>
          </div>
          {/* Corpo do email */}
          <div className="px-3 py-3">
            <div className="text-[13px] text-slate-700 dark:text-slate-300 whitespace-pre-wrap leading-relaxed break-words min-w-0">
              {messageText ? (
                formatMessageWithVariables(messageText)
              ) : (
                <span className="text-slate-400 italic text-xs">
                  Digite a mensagem...
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PREVIEW - TELEGRAM
// ============================================================================

function TelegramPreview({ messageText, inboxName }: { messageText: string; inboxName?: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header Telegram */}
      <div className="flex items-center gap-2 px-3 py-2 bg-[#0088cc] text-white rounded-t-lg">
        <Send className="w-4 h-4" />
        <p className="text-xs font-medium">{inboxName || 'Telegram'}</p>
      </div>

      {/* Área de chat Telegram */}
      <div className="flex-1 p-3 bg-[#c8d9ea] dark:bg-slate-800 overflow-y-auto min-h-[200px]">
        <div className="max-w-[240px] ml-auto">
          <div className="bg-[#effdde] rounded-lg rounded-br-sm px-3 py-2 shadow-sm">
            <div className="text-[13px] text-slate-900 whitespace-pre-wrap leading-relaxed break-words min-w-0">
              {messageText ? (
                formatMessageWithVariables(messageText)
              ) : (
                <span className="text-slate-400 italic text-xs">
                  Digite a mensagem...
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-500 text-right mt-0.5">{getCurrentTime()} ✓</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PREVIEW - GENÉRICO (Instagram, outros)
// ============================================================================

function GenericPreview({ messageText, channelType, inboxName }: { messageText: string; channelType?: string; inboxName?: string }) {
  return (
    <div className="flex flex-col h-full">
      {/* Header genérico */}
      <div className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-t-lg">
        <Monitor className="w-4 h-4" />
        <p className="text-xs font-medium">{inboxName || channelType || 'Canal'}</p>
      </div>

      {/* Área de mensagens genérica */}
      <div className="flex-1 p-3 bg-slate-100 dark:bg-slate-800 overflow-y-auto min-h-[200px]">
        <div className="max-w-[240px] ml-auto">
          <div className="bg-white dark:bg-slate-700 rounded-xl px-3 py-2 shadow-sm border border-slate-200 dark:border-slate-600">
            <div className="text-[13px] text-slate-800 dark:text-slate-200 whitespace-pre-wrap leading-relaxed break-words min-w-0">
              {messageText ? (
                formatMessageWithVariables(messageText)
              ) : (
                <span className="text-slate-400 italic text-xs">
                  Digite a mensagem...
                </span>
              )}
            </div>
            <p className="text-[10px] text-slate-400 text-right mt-1">{getCurrentTime()}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// PREVIEW - TEMPLATE HSM (quando tipo = template)
// ============================================================================

/**
 * Formata body do template WABA substituindo variáveis posicionais {{1}}, {{2}}
 * pelos valores de exemplo da variável mapeada (ex: {{nome_completo}} → "Maria Silva")
 */
function formatTemplateBody(
  bodyText: string,
  variableMapping?: Record<string, string>
): React.ReactNode {
  if (!bodyText) return null;

  // Regex para capturar variáveis posicionais {{1}}, {{2}}, etc.
  const parts = bodyText.split(/(\{\{\d+\}\})/g);

  return parts.map((part, index) => {
    const posMatch = part.match(/^\{\{(\d+)\}\}$/);
    if (posMatch) {
      const posKey = posMatch[1]; // "1", "2", etc.
      const mappedVar = variableMapping?.[posKey]; // ex: "{{nome_completo}}"

      if (mappedVar) {
        // Variável mapeada — mostra valor de exemplo
        const example = VARIABLE_EXAMPLES[mappedVar] || mappedVar.replace(/\{\{|\}\}/g, '');
        return (
          <span
            key={index}
            className="bg-emerald-200/80 text-emerald-900 px-1 rounded text-xs font-medium"
            title={`{{${posKey}}} → ${mappedVar}`}
          >
            {example}
          </span>
        );
      }

      // Variável posicional NÃO mapeada — destaque de alerta
      return (
        <span
          key={index}
          className="bg-orange-200/80 text-orange-900 px-1 rounded text-xs font-mono"
          title={`Variável {{${posKey}}} não mapeada`}
        >
          {`{{${posKey}}}`}
        </span>
      );
    }

    // Também tratar variáveis nomeadas {{var}} que possam existir no body
    if (/\{\{[a-z_]+\}\}/i.test(part)) {
      return formatMessageWithVariables(part);
    }

    return part;
  });
}

function TemplateHsmPreview({
  templateName,
  bodyText,
  variableMapping,
  inboxName,
}: {
  templateName?: string;
  bodyText?: string;
  variableMapping?: Record<string, string>;
  inboxName?: string;
}) {
  // Se tem body text do template, renderiza preview interativo estilo WhatsApp
  if (bodyText) {
    return (
      <div className="flex flex-col h-full">
        {/* Barra superior WhatsApp */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#075E54] text-white rounded-t-lg">
          <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <MessageSquare className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium truncate">{inboxName || 'WhatsApp Oficial'}</p>
            <p className="text-[10px] opacity-70">online</p>
          </div>
        </div>

        {/* Área de chat */}
        <div
          className="flex-1 p-3 overflow-y-auto min-h-[200px]"
          style={{
            backgroundColor: '#e5ddd5',
            backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ccc\' fill-opacity=\'0.08\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          }}
        >
          <div className="max-w-[260px] ml-auto">
            {/* Badge HSM Template */}
            <div className="flex justify-end mb-1">
              <span className="text-[9px] bg-emerald-600 text-white px-1.5 py-0.5 rounded-full font-medium">
                📋 HSM Template: {templateName}
              </span>
            </div>

            {/* Balão de mensagem */}
            <div className="bg-[#dcf8c6] rounded-lg shadow-sm p-2.5 relative">
              <div className="text-[13px] text-slate-900 whitespace-pre-wrap leading-relaxed break-words min-w-0">
                {formatTemplateBody(bodyText, variableMapping)}
              </div>
              {/* Timestamp + check */}
              <div className="flex items-center justify-end gap-1 mt-1">
                <span className="text-[10px] text-slate-500">{getCurrentTime()}</span>
                <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback: nenhum template selecionado ainda — placeholder estático
  return (
    <div className="flex flex-col items-center justify-center h-full p-4 text-center bg-slate-50 dark:bg-slate-800 min-h-[200px]">
      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
        <MessageSquare className="w-6 h-6 text-primary" />
      </div>
      <p className="text-sm font-medium text-foreground">Template HSM</p>
      <p className="text-xs text-muted-foreground mt-1 max-w-[200px]">
        Selecione um template aprovado para visualizar o preview da mensagem.
      </p>
    </div>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function ReminderMessagePreview({
  channelType,
  inboxName,
  messageText,
  messageType,
  templateName,
  variableMapping,
  attachment,

}: ReminderMessagePreviewProps) {
  // Se tipo = template HSM, mostra preview interativo com body do template
  if (messageType === 'template') {
    return (
      <TemplateHsmPreview
        templateName={templateName}
        bodyText={messageText}
        variableMapping={variableMapping}
        inboxName={inboxName}
      />
    );
  }

  // Seleciona preview pelo tipo de canal
  const normalizedChannel = channelType?.toLowerCase() || '';

  if (normalizedChannel.includes('whatsapp') || normalizedChannel === 'waba') {
    return <WhatsAppPreview messageText={messageText} inboxName={inboxName} attachment={attachment} />;
  }

  if (normalizedChannel.includes('sms')) {
    return <SmsPreview messageText={messageText} inboxName={inboxName} />;
  }

  if (normalizedChannel.includes('email')) {
    return <EmailPreview messageText={messageText} inboxName={inboxName} />;
  }

  if (normalizedChannel.includes('telegram')) {
    return <TelegramPreview messageText={messageText} inboxName={inboxName} />;
  }

  // Instagram, webchat, outros — preview genérico
  return <GenericPreview messageText={messageText} channelType={channelType} inboxName={inboxName} />;
}
